import { useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Sparkles,
  Calendar,
  Users,
  Award,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

const FORMATS = ["MUN", "Debate", "Youth Parliament", "SDG / Sustainability", "Hackathon", "Leadership Summit", "Other"];
const SIZES = ["Under 100 delegates", "100 – 300", "300 – 800", "800 – 1500", "1500+"];
const TIMINGS = ["Within 30 days", "1 – 3 months", "3 – 6 months", "Just exploring"];

const HIGHLIGHTS = [
  { icon: Calendar, label: "20-minute live tour", note: "We share screen, you ask anything." },
  { icon: Users, label: "Stakeholder walkthrough", note: "Bring your secretariat & faculty advisor." },
  { icon: Award, label: "Free pilot conference", note: "We help you ship your next event end-to-end." },
  { icon: ShieldCheck, label: "Security & DPA", note: "We send you our SOC-2 evidence pack." },
];

export default function DemoPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    role: "",
    format: "MUN",
    delegate_size: "100 – 300",
    timing: "1 – 3 months",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api
        .post("/demo-request", form)
        .catch(() => {
          /* mock-friendly: still mark done */
        });
      setDone(true);
      toast.success("Demo request received — we'll send a calendar invite shortly");
    } catch {
      toast.error("Could not submit. Email info@factskoot.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-brand-navy-dark text-brand-champagne">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-dark via-brand-navy/95 to-brand-navy-dark/75" />
        <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
        <div aria-hidden className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/5 border border-brand-gold/30 mb-6">
            <Sparkles className="h-3 w-3 text-brand-gold" />
            <span className="eyebrow">Live demo · Free pilot · Zero pressure</span>
          </div>
          <h1 className="headline-luxury text-4xl sm:text-5xl lg:text-[4.5rem] text-brand-champagne max-w-4xl">
            See Diplomexa run a{" "}
            <span className="italic font-display font-medium text-brand-gold">600-delegate</span>{" "}
            conference live.
          </h1>
          <div className="divider-gold-hairline mt-8 max-w-md" />
          <p className="mt-8 text-base lg:text-lg text-brand-champagne/70 max-w-2xl leading-relaxed">
            We&apos;ll log into a real secretariat, walk through registrations, live voting,
            paper evaluation and certificate delivery — and answer every question on the call.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-7">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              What you'll get
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tighter text-[#0A192F]">
              A demo that respects your time.
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Most calls take 20 minutes. We never trap you in a 60-minute pitch deck.
            </p>
          </div>
          <div className="space-y-3">
            {HIGHLIGHTS.map((h) => {
              const Icon = h.icon;
              return (
                <div key={h.label} className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 bg-white">
                  <div className="h-10 w-10 rounded-md bg-[#0A192F] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#0A192F]">{h.label}</p>
                    <p className="mt-1 text-sm text-slate-600">{h.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 lg:p-9 shadow-sm">
            {done ? (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <ArrowRight className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold tracking-tight text-[#0A192F]">
                  Demo locked in.
                </h3>
                <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                  Watch your inbox for a calendar invite within the next business day.
                  In the meantime, browse the live console with our demo accounts.
                </p>
                <div className="mt-6 flex justify-center gap-3">
                  <Link to="/login">
                    <Button className="bg-[#0F4C81] hover:bg-[#0A3860] text-white">
                      Try the demo console
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white">
                      Back to home
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Request demo
                </p>
                <h2 className="text-2xl font-bold tracking-tight text-[#0A192F]">
                  Tell us about your conference.
                </h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Full name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="demo-name" />
                  </div>
                  <div>
                    <Label>Work email</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="demo-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Organisation / school</Label>
                    <Input required value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} data-testid="demo-org" />
                  </div>
                  <div>
                    <Label>Your role</Label>
                    <Input value={form.role} placeholder="Secretary-General, Faculty advisor, Founder" onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="demo-role" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Format</Label>
                    <Selector value={form.format} options={FORMATS} onChange={(v) => setForm({ ...form, format: v })} testid="demo-format" />
                  </div>
                  <div>
                    <Label>Conference size</Label>
                    <Selector value={form.delegate_size} options={SIZES} onChange={(v) => setForm({ ...form, delegate_size: v })} testid="demo-size" />
                  </div>
                  <div>
                    <Label>When?</Label>
                    <Selector value={form.timing} options={TIMINGS} onChange={(v) => setForm({ ...form, timing: v })} testid="demo-timing" />
                  </div>
                </div>
                <div>
                  <Label>Anything specific you want us to show?</Label>
                  <Textarea
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Optional — e.g., AI paper evaluation, live voting, sponsor portal."
                    data-testid="demo-notes"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold uppercase tracking-[0.18em]"
                  data-testid="demo-submit"
                >
                  {submitting ? "Sending..." : "Book my demo"}
                </Button>
                <p className="text-[11px] text-slate-500">
                  We'll respond within one business day with a calendar invite. We never spam — read our privacy policy.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function Selector({ value, options, onChange, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:border-[#0F4C81]"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}
